(() => {
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const dataUrlToBlob = (dataUrl) => {
    const [metadata, encoded] = dataUrl.split(',');
    const mimeType = metadata.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: mimeType });
  };
  const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const loadImage = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('De afbeelding kon niet worden geladen.'));
    image.src = source;
  });
  const addWatermark = async (source, watermarkSource, colourCode) => {
    const [image, watermark] = await Promise.all([loadImage(source), loadImage(watermarkSource)]);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Het watermerk kon niet worden toegevoegd.');
    context.drawImage(image, 0, 0);
    const logoWidth = Math.min(220, Math.max(140, canvas.width * .22));
    const logoHeight = logoWidth * (watermark.naturalHeight / watermark.naturalWidth);
    const padding = Math.max(10, canvas.width * .012);
    const x = (canvas.width - logoWidth) / 2;
    const y = canvas.height - logoHeight - padding * 2;
    context.fillStyle = 'rgba(24, 20, 18, .58)';
    context.beginPath();
    context.roundRect(x - padding, y - padding, logoWidth + padding * 2, logoHeight + padding * 2, padding);
    context.fill();
    context.globalAlpha = .94;
    context.drawImage(watermark, x, y, logoWidth, logoHeight);
    context.globalAlpha = 1;
    const colourLabel = `COLOR ${colourCode}`;
    const fontSize = Math.max(18, Math.round(canvas.width * .024));
    const labelPaddingX = Math.round(fontSize * .45);
    const labelPaddingY = Math.round(fontSize * .32);
    const labelMargin = Math.max(10, Math.round(canvas.width * .01));
    context.font = `400 ${fontSize}px Arial, sans-serif`;
    context.textBaseline = 'middle';
    const labelWidth = Math.ceil(context.measureText(colourLabel).width) + labelPaddingX * 2;
    const labelHeight = fontSize + labelPaddingY * 2;
    const labelX = canvas.width - labelWidth - labelMargin;
    const labelY = labelMargin;
    context.fillStyle = 'rgba(12, 12, 12, .94)';
    context.fillRect(labelX, labelY, labelWidth, labelHeight);
    context.fillStyle = '#ffffff';
    context.fillText(colourLabel, labelX + labelPaddingX, labelY + labelHeight / 2);
    return canvas.toDataURL('image/jpeg', .92);
  };
  const readJson = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'De aanvraag is mislukt.');
    return data;
  };
  const resizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('De foto kon niet worden gelezen.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Dit is geen geldige foto.'));
      image.onload = () => {
        const max = 1600;
        const scale = Math.min(1, max / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext('2d');
        if (!context) return reject(new Error('De foto kon niet worden voorbereid.'));
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', .9));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const initialise = (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const form = root.querySelector('[data-try-on-form]');
    if (!form) return;
    const input = root.querySelector('[data-file-input]');
    const dropzone = root.querySelector('[data-dropzone]');
    const preview = root.querySelector('[data-photo-preview]');
    const fileLabel = root.querySelector('[data-file-label]');
    const consent = root.querySelector('[data-consent]');
    const submit = root.querySelector('[data-submit]');
    const status = root.querySelector('[data-status]');
    const result = root.querySelector('[data-result]');
    const resultImage = root.querySelector('[data-result-image]');
    const download = root.querySelector('[data-download]');
    const retry = root.querySelector('[data-retry]');
    let photo = '';
    let resultFile;
    let resultObjectUrl = '';

    const clearDownload = () => {
      if (resultObjectUrl) URL.revokeObjectURL(resultObjectUrl);
      resultObjectUrl = '';
      resultFile = undefined;
      download.removeAttribute('href');
    };

    const setStatus = (message, error = false) => {
      status.textContent = message;
      status.classList.toggle('is-error', error);
    };
    const updateSubmit = () => { submit.disabled = !photo || !consent.checked; };
    const loadFile = async (file) => {
      if (!file || !['image/jpeg','image/png','image/webp'].includes(file.type)) {
        photo = ''; preview.hidden = true; fileLabel.textContent = root.dataset.uploadLabel; updateSubmit();
        return setStatus(root.dataset.error, true);
      }
      try {
        setStatus(root.dataset.processing);
        photo = await resizeImage(file);
        preview.src = photo; preview.hidden = false; fileLabel.textContent = file.name;
        setStatus(''); updateSubmit();
      } catch (_) { photo = ''; preview.hidden = true; setStatus(root.dataset.error, true); updateSubmit(); }
    };

    input.addEventListener('change', () => loadFile(input.files[0]));
    consent.addEventListener('change', updateSubmit);
    ['dragenter','dragover'].forEach((event) => dropzone.addEventListener(event, (e) => { e.preventDefault(); dropzone.classList.add('is-dragging'); }));
    ['dragleave','drop'].forEach((event) => dropzone.addEventListener(event, (e) => { e.preventDefault(); dropzone.classList.remove('is-dragging'); }));
    dropzone.addEventListener('drop', (event) => loadFile(event.dataTransfer.files[0]));
    download.addEventListener('click', async (event) => {
      if (!resultFile || !isMobileDevice()) return;
      event.preventDefault();
      const canShareFile = typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [resultFile] });
      if (canShareFile) {
        try {
          await navigator.share({ files: [resultFile], title: 'Mijn Fee Extensions-look' });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
          window.location.href = resultObjectUrl;
          return;
        }
      }
      window.open(resultObjectUrl, '_blank', 'noopener,noreferrer');
    });
    retry.addEventListener('click', () => {
      clearDownload();
      result.hidden = true;
      form.hidden = false;
      resultImage.removeAttribute('src');
      window.scrollTo({ top: root.offsetTop - 24, behavior: 'smooth' });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!photo || !consent.checked) return;
      submit.disabled = true;
      setStatus(root.dataset.processing);
      try {
        const colourInput = form.querySelector('input[name="colour"]:checked');
        const colour = colourInput.value;
        const colourCode = colourInput.closest('label')?.querySelector('strong')?.textContent.trim()
          || `#${colour.replace('_', '/')}`;
        const length = form.querySelector('input[name="length"]:checked').value;
        const created = await readJson(await fetch(`${root.dataset.endpoint}/generate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image: photo, colour, length })
        }));
        let completed;
        for (let attempt = 0; attempt < 90; attempt += 1) {
          await wait(2000);
          const polled = await readJson(await fetch(`${root.dataset.endpoint}/status?job=${encodeURIComponent(created.job)}`, { cache: 'no-store' }));
          if (polled.status === 'completed') { completed = polled; break; }
        }
        if (!completed) throw new Error('Het maken van de foto duurde te lang. Probeer het opnieuw.');
        const watermarkedImage = await addWatermark(completed.image, root.dataset.watermark, colourCode);
        clearDownload();
        const resultBlob = dataUrlToBlob(watermarkedImage);
        resultFile = new File([resultBlob], 'fee-extensions-virtual-try-on.jpg', { type: 'image/jpeg' });
        resultObjectUrl = URL.createObjectURL(resultBlob);
        resultImage.src = watermarkedImage;
        download.href = resultObjectUrl;
        form.hidden = true;
        result.hidden = false;
        setStatus('');
        result.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        setStatus(error.message || root.dataset.error, true);
        submit.disabled = false;
      }
    });
  };

  const initialiseAll = () => document.querySelectorAll('[data-fee-try-on]').forEach(initialise);
  initialiseAll();
  document.addEventListener('shopify:section:load', initialiseAll);
})();
